import ReactDOM from "react-dom/client";
import { useEffect, useMemo, useRef } from "react";
import CallDispositionPopup from "../../components/leads/CallDispositionPopup";
import DuplicateAlertModal from "../../components/leads/DuplicateAlertModal";
import LeadFilters, { CITY_REGION_MAP } from "../../components/leads/LeadFilters";
import LeadFormModal from "../../components/leads/LeadFormModal";
import LeadJourneyModal from "../../components/leads/LeadJourneyModal";
import LeadJourneyPickerModal from "../../components/leads/LeadJourneyPickerModal";
import LeadsTable from "../../components/leads/LeadsTable";
import BulkUploadModal from "../../components/leads/BulkUploadModal";
import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  INITIAL_LEAD_FORM,
  PAGE_SIZE_OPTIONS,
} from "../../lib/constants";
import {
  buildLeadRequestPayload,
  downloadViaBrowser,
  filterDuplicateMatchesForRole,
  getCityOptionsForRegion,
  resolveGeographyNode,
  toInputDateTime,
  validateDocumentSelection,
} from "../../lib/utils";
import { fetchJson } from "../../lib/fetchJson";
import { INITIAL_DUPLICATE_STATE, INITIAL_DOCUMENT_FEEDBACK } from "../../hooks/useLeadModals";

const MAX_LEAD_DOCUMENTS = 4;

export function LeadsPage({
  // from useLeads
  agents,
  assignableAgents,
  bulkUploadingRef,
  clearLeadFilters,
  deferredSearch,
  dispositionPopupRef,
  filters,
  followUps,
  geographyData,
  handleLeadFilterChange,
  leadPageSize,
  leadResponse,
  loading,
  paginationItems,
  refreshSelectedLead,
  rootDispositions,
  search,
  selectedLead,
  selectedLeadId,
  setAssignableAgents,
  setFilters,
  setLeadPageSize,
  setLoading,
  setSearch,
  setSelectedLead,
  setSelectedLeadId,
  loadWorkspace,

  // from useLeadModals
  bulkFeedback,
  bulkForm,
  bulkModal,
  bulkResult,
  closeBulkModal,
  documentFeedback,
  duplicateState,
  guideOpen,
  handleBulkFormChange,
  journeyModalOpen,
  journeyPickerOpen,
  leadForm,
  leadModal,
  openBulkReassign,
  openBulkUpload,
  setBulkFeedback,
  setBulkForm,
  setBulkModal,
  setBulkResult,
  setDocumentFeedback,
  setDuplicateState,
  setGuideOpen,
  setJourneyModalOpen,
  setJourneyPickerOpen,
  setLeadForm,
  setLeadModal,

  // from useCRMContext / parent
  context,
  loadContext,
  loadIntegrationDiagnostics,
  integrationDiagnostics,
  loadReports,

  // from auth / parent
  currentUser,
  roleScope,
  setError,
  setNotice,
  activeSidebarItem,
  activeTopNav,
  setLastImportAlert,
  setLastSyncAlert,
  pendingCreateLeadAgent,
  pendingCallLeadId,
  onPendingCreateLeadHandled,
  onPendingCallHandled,
  onExportJobQueued,
  showContent = true,
}) {
  const INITIAL_BULK_FEEDBACK = { error: "", notice: "" };

  const canManageBulkImports = Boolean(roleScope?.can_bulk_upload);
  const availableFilterSubDispositions = useMemo(() => {
    const selectedIds = String(filters.dispositionRootId || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (!selectedIds.length) return [];
    const selected = rootDispositions.filter((item) =>
      selectedIds.includes(String(item.id)),
    );
    return selected.flatMap((item) => item.children || []);
  }, [filters.dispositionRootId, rootDispositions]);

  const filterCityOptions = useMemo(
    () =>
      getCityOptionsForRegion(
        geographyData.cityOptionsByRegion,
        filters.region,
        geographyData.allCities,
      ),
    [filters.region, geographyData],
  );

  const leadCityOptions = useMemo(
    () =>
      getCityOptionsForRegion(
        geographyData.cityOptionsByRegion,
        leadForm.regionName,
        geographyData.allCities,
      ),
    [geographyData, leadForm.regionName],
  );

  const selectedLeadFormGeography = useMemo(
    () =>
      resolveGeographyNode({
        cityName: leadForm.cityName,
        geographyData,
        regionName: leadForm.regionName,
      }),
    [geographyData, leadForm.cityName, leadForm.regionName],
  );

  const leadModalAgents = useMemo(() => {
    if (roleScope?.scope === "own_leads") return agents;

    const mergedAgents = [...assignableAgents];
    const selectedAgent = agents.find(
      (agent) => String(agent.id) === String(leadForm.assignedToId || ""),
    );

    if (
      selectedAgent &&
      !mergedAgents.some((agent) => String(agent.id) === String(selectedAgent.id))
    ) {
      mergedAgents.push(selectedAgent);
    }

    return mergedAgents.length ? mergedAgents : agents;
  }, [agents, assignableAgents, leadForm.assignedToId, roleScope?.scope]);

  const sourceMap = useMemo(
    () =>
      Object.fromEntries(
        (context.lead_sources || []).map((source) => [String(source.id), source]),
      ),
    [context.lead_sources],
  );

  // Keep a ref to agents so the fetch effect can use it as a fallback
  // without adding it to the dep array (would cause infinite re-runs because
  // setLoading triggers a parent re-render which creates a new agents array).
  const agentsRef = useRef(agents);
  useEffect(() => { agentsRef.current = agents; }, [agents]);

  // Sync assignable agents when lead modal opens
  useEffect(() => {
    if (!leadModal.open || roleScope?.scope === "own_leads" || !currentUser) {
      return undefined;
    }

    let active = true;

    async function syncAssignableAgents() {
      try {
        setLoading((current) => ({ ...current, assignableAgents: true }));
        const params = new URLSearchParams();
        if (selectedLeadFormGeography?.id) {
          params.set("geography_id", String(selectedLeadFormGeography.id));
        }
        const query = params.toString();
        const payload = await fetchJson(
          `/api/crm/manager/agents/${query ? `?${query}` : ""}`,
        );
        if (!active) return;
        setAssignableAgents(
          (payload.agents || []).map((agent) => ({
            full_name: agent.name,
            id: agent.id,
            role: "agent",
          })),
        );
      } catch (requestError) {
        if (active) {
          // Use ref so we always have the latest agents without making agents
          // a dependency (which would re-run this effect on every parent render).
          setAssignableAgents(agentsRef.current);
          if (!requestError?.isNetworkError) setError(requestError.message);
        }
      } finally {
        if (active) {
          setLoading((current) => ({ ...current, assignableAgents: false }));
        }
      }
    }

    syncAssignableAgents();

    return () => {
      active = false;
    };
  }, [
    currentUser,
    leadModal.open,
    roleScope?.scope,
    selectedLeadFormGeography?.id,
  ]); // agents intentionally excluded — using agentsRef to avoid infinite re-runs

  // Clear document feedback when journey closes
  useEffect(() => {
    if (!journeyModalOpen) {
      setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
    }
  }, [journeyModalOpen]);

  // Handle ?call= query param
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    const callLeadId = params.get("call");
    if (!callLeadId) return;
    window.history.replaceState({}, "", window.location.pathname);
    (async () => {
      try {
        const detail = await refreshSelectedLead(callLeadId);
        setSelectedLeadId(String(callLeadId));
        openDispositionPopup(detail);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !pendingCreateLeadAgent) {
      return;
    }

    openCreateLeadModal(pendingCreateLeadAgent);
    onPendingCreateLeadHandled?.();
  }, [currentUser, pendingCreateLeadAgent]);

  useEffect(() => {
    if (!currentUser || !pendingCallLeadId) {
      return;
    }

    openCallModal(pendingCallLeadId).finally(() => {
      onPendingCallHandled?.();
    });
  }, [currentUser, pendingCallLeadId]);

  function handleLeadFormChange(field, value) {
    setLeadForm((current) => {
      const next = { ...current, [field]: value };

      if (field === "regionName") {
        const nextCityOptions = getCityOptionsForRegion(
          geographyData.cityOptionsByRegion,
          value,
          geographyData.allCities,
        );
        if (current.cityName && !nextCityOptions.includes(current.cityName)) {
          next.cityName = "";
        }
      }

      if (field === "cityName" && value) {
        const mappedRegion = CITY_REGION_MAP[value];
        if (mappedRegion) {
          next.regionName = mappedRegion;
        }
      }

      if (field === "dispositionId") {
        next.subDispositionId = "";
        next.nextFollowUp = "";
      }

      return next;
    });
  }

  function openCreateLeadModal(agentPreset = null) {
    if (!roleScope?.can_create_lead) return;
    const geographies = agentPreset?.geographies || [];
    let presetRegionName = "";
    let presetCityName = "";

    geographies.forEach((geo) => {
      if (!presetRegionName && geo.node_type === "region") {
        presetRegionName = geo.name || "";
      }
      if (!presetCityName && geo.node_type === "city") {
        presetCityName = geo.name || "";
      }
      if (!presetRegionName && geo.display_name) {
        presetRegionName = String(geo.display_name).split(" / ")[0] || "";
      }
    });

    setNotice("");
    setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
    setDuplicateState(INITIAL_DUPLICATE_STATE);
    const btlSource =
      currentUser?.role === "btl"
        ? (context.lead_sources || []).find((s) => s.source_type === "btl")
        : null;
    setLeadForm({
      ...INITIAL_LEAD_FORM,
      assignedToId:
        roleScope.scope === "own_leads"
          ? String(currentUser.id)
          : agentPreset?.id
          ? String(agentPreset.id)
          : "",
      regionName: presetRegionName || currentUser?.primary_region_name || "",
      cityName: presetCityName || currentUser?.primary_city_name || "",
      nextFollowUp: "",
      sourceId: btlSource ? String(btlSource.id) : "",
    });
    setLeadModal({ open: true, mode: "create" });
  }

  async function openEditLeadModal(leadId) {
    if (!roleScope?.can_edit_lead) return;
    try {
      setError("");
      setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
      const detail =
        String(selectedLeadId) === String(leadId) && selectedLead
          ? selectedLead
          : await refreshSelectedLead(leadId);
      setSelectedLeadId(String(leadId));
      const savedWhatsapp = detail?.whatsapp_number || "";
      const isSameAsMobile = !savedWhatsapp || savedWhatsapp === (detail?.phone_number || "");
      setLeadForm({
        fullName: detail?.full_name || "",
        phoneNumber: detail?.phone_number || "",
        whatsappSameAsMobile: isSameAsMobile,
        whatsappNumber: isSameAsMobile ? "" : savedWhatsapp,
        email: detail?.email || "",
        subSource: detail?.sub_source || "",
        profession: detail?.profession || "",
        sourceId: String(detail?.source || ""),
        assignedToId: String(detail?.assigned_to || ""),
        regionName: detail?.region_name || "",
        cityName: detail?.city_name || "",
        pincode: detail?.pincode || "",
        notes: detail?.notes || "",
        dispositionId: String(detail?.current_disposition || ""),
        subDispositionId: String(detail?.current_sub_disposition || ""),
        nextFollowUp: toInputDateTime(detail?.next_follow_up_at),
      });
      setLeadModal({ open: true, mode: "edit" });
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function openCallModal(leadId) {
    if (!roleScope?.can_call) return;

    if (dispositionPopupRef.current && !dispositionPopupRef.current.closed) {
      dispositionPopupRef.current.close();
    }
    const popup = window.open("about:blank", "_blank");
    window.open("https://agent.cloudagent.ozonetel.com/", "_blank");

    try {
      setError("");
      const detail =
        String(selectedLeadId) === String(leadId) && selectedLead
          ? selectedLead
          : await refreshSelectedLead(leadId);
      setSelectedLeadId(String(leadId));
      if (popup) openDispositionPopup(detail, popup);
    } catch (requestError) {
      setError(requestError.message);
      if (popup) popup.close();
    }
  }

  function openDispositionPopup(lead, preOpenedPopup) {
    const popup = preOpenedPopup || (() => {
      if (dispositionPopupRef.current && !dispositionPopupRef.current.closed) {
        dispositionPopupRef.current.close();
      }
      return window.open("about:blank", "_blank");
    })();
    if (!popup) return;

    dispositionPopupRef.current = popup;

    popup.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1.0"/>
  <title>Call – ${lead?.full_name || "Session"}</title>
</head>
<body><div id="crm-disp-root"></div></body>
</html>`);
    popup.document.close();

    const crmStartCall = async (leadId, agentId) =>
      fetchJson(`/api/crm/leads/${leadId}/call-client/`, {
        method: "POST",
        body: JSON.stringify({ agent_id: agentId || undefined }),
      });
    const crmSyncCall = async (leadId) => refreshSelectedLead(leadId);
    const crmSaveDisposition = async (leadId, formData) =>
      handleInlineDispositionSave(leadId, formData, lead);
    const crmDisconnectCall = async (leadId, callLogId) => {
      await fetchJson(`/api/crm/leads/${leadId}/disconnect-call/`, {
        method: "POST",
        body: JSON.stringify({ call_log_id: callLogId || undefined }),
      });
      await refreshSelectedLead(leadId);
    };

    const root = ReactDOM.createRoot(popup.document.getElementById("crm-disp-root"));
    root.render(
      <CallDispositionPopup
        lead={lead}
        agents={agents}
        defaultAgentId={
          roleScope?.scope === "own_leads"
            ? String(currentUser?.id || "")
            : String(lead?.assigned_to || "")
        }
        lockAgent={roleScope?.scope === "own_leads"}
        dispositionGroups={context.disposition_groups}
        fetchJson={fetchJson}
        onStartCall={crmStartCall}
        onSyncCall={crmSyncCall}
        onSaveDisposition={crmSaveDisposition}
        onDisconnectCall={crmDisconnectCall}
        isAdmin={currentUser?.role === "admin"}
      />,
    );
  }

  async function openJourneyModal(leadId) {
    try {
      setError("");
      if (!leadId) return;
      setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
      const detail =
        String(selectedLeadId) === String(leadId) && selectedLead
          ? selectedLead
          : await refreshSelectedLead(leadId);
      setSelectedLeadId(String(leadId));
      setSelectedLead(detail);
      setJourneyModalOpen(true);
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function handleJourneyPickerConfirm(leadId) {
    setJourneyPickerOpen(false);
    await openJourneyModal(leadId);
  }

  function buildLeadPayload() {
    return buildLeadRequestPayload({
      currentUser,
      leadForm,
      mode: leadModal.mode,
      roleScope,
    });
  }

  async function submitLeadPayload(payload, options = {}) {
    const { skipDuplicateCheck = false } = options;

    try {
      setError("");
      setLoading((current) => ({ ...current, submittingLead: true }));

      if (leadModal.mode === "create" && !skipDuplicateCheck) {
        const duplicatePreview = await fetchJson("/api/crm/leads/duplicate_check/", {
          method: "POST",
          body: JSON.stringify({
            phone_number: payload.phone_number,
            email: payload.email,
            region_name: payload.region_name,
            city_name: payload.city_name,
          }),
        });
        if (duplicatePreview.duplicate_detected) {
          const previewMatches = filterDuplicateMatchesForRole(
            duplicatePreview.duplicate_matches || [],
            payload,
            roleScope,
          );
          setDuplicateState({
            ...INITIAL_DUPLICATE_STATE,
            open: true,
            matches: previewMatches,
            payload,
            variant: "preview",
            message:
              "Matching contact details already exist. Review the visible matches before you continue.",
          });
          return;
        }
      }

      let responsePayload;
      if (leadModal.mode === "create") {
        responsePayload = await fetchJson("/api/crm/leads/", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const responseMatches = filterDuplicateMatchesForRole(
          responsePayload?.duplicate_matches || [],
          responsePayload,
          roleScope,
        );
        const duplicateDetected = Boolean(responsePayload?.duplicate_detected);
        const reusedExisting = Boolean(responsePayload?.reused_existing);

        setNotice(
          reusedExisting
            ? "Existing lead reused and history updated."
            : duplicateDetected
            ? "Lead created with duplicate history logged."
            : "Lead created successfully.",
        );

        if (reusedExisting || duplicateDetected) {
          setDuplicateState({
            ...INITIAL_DUPLICATE_STATE,
            leadId: responsePayload?.id || null,
            matches: responseMatches,
            message: reusedExisting
              ? "An existing lead in the same geography was reused. Re-add and source history were updated."
              : responseMatches.length
              ? "The lead was created and duplicate history was logged for the matching records shown here."
              : "The lead was created and duplicate history was logged, but no matching records are visible in your current duplicate scope.",
            open: true,
            variant: reusedExisting ? "reused" : "created",
          });
        }
      } else {
        responsePayload = await fetchJson(`/api/crm/leads/${selectedLeadId}/`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        setNotice("Lead updated successfully.");
      }

      setLeadModal({ open: false, mode: "create" });
      setFilters((current) => ({ ...current, page: 1 }));
      await Promise.all([loadWorkspace(), loadContext()]);

      if (responsePayload?.id) {
        const responseLeadId = String(responsePayload.id);
        setSelectedLeadId(responseLeadId);
        await refreshSelectedLead(responseLeadId);
      }
      if (
        leadModal.mode !== "create" ||
        (!responsePayload?.duplicate_detected && !responsePayload?.reused_existing)
      ) {
        setDuplicateState(INITIAL_DUPLICATE_STATE);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading((current) => ({ ...current, submittingLead: false }));
    }
  }

  async function handleLeadSubmit(event) {
    event.preventDefault();
    await submitLeadPayload(buildLeadPayload());
  }

  async function handleDeleteLead(leadId) {
    if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
    try {
      setError("");
      await fetchJson(`/api/crm/leads/${leadId}/`, { method: "DELETE" });
      setNotice("Lead deleted.");
      await loadWorkspace();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function handleDuplicateConfirm() {
    if (!duplicateState.payload) return;
    await submitLeadPayload(duplicateState.payload, { skipDuplicateCheck: true });
  }

  async function handleBulkSubmit(event) {
    event.preventDefault();
    if (!bulkForm.file) {
      setBulkFeedback({ error: "Upload file is required.", notice: "" });
      return;
    }

    try {
      setError("");
      setBulkFeedback(INITIAL_BULK_FEEDBACK);
      setLoading((current) => ({ ...current, submittingBulk: true }));
      bulkUploadingRef.current = true;
      const formData = new FormData();
      formData.append("file", bulkForm.file);
      if (bulkForm.sourceId) formData.append("source_id", bulkForm.sourceId);
      if (bulkForm.importType) formData.append("import_type", bulkForm.importType);

      const endpoint =
        bulkModal.mode === "reassign"
          ? "/api/crm/leads/bulk-reassign/"
          : "/api/crm/leads/bulk-import/";
      const payload = await fetchJson(endpoint, {
        method: "POST",
        body: formData,
      });
      setBulkResult(payload);
      setBulkFeedback({
        error: "",
        notice:
          payload.invalid_rows > 0
            ? "Bulk file processed. Review invalid rows below before the next upload."
            : "Bulk file processed successfully.",
      });
      // Propagate import alert up via callback if provided
      if (typeof setLastImportAlert === "function") {
        setLastImportAlert(
          payload.invalid_rows > 0
            ? {
                id: "latest-import-invalid-rows",
                tone: "warning",
                title: "Bulk import needs review",
                description: `${payload.invalid_rows} invalid row${payload.invalid_rows === 1 ? "" : "s"} found in the latest upload.`,
              }
            : null,
        );
      }
      setNotice("Bulk file processed successfully.");
      await Promise.all([loadWorkspace(), loadContext(), loadReports && loadReports(currentUser, { b2cOnly: true })]);
    } catch (requestError) {
      setBulkFeedback({ error: requestError.message, notice: "" });
      if (typeof setLastImportAlert === "function") {
        setLastImportAlert({
          id: "latest-import-error",
          tone: "danger",
          title: "Bulk import failed",
          description: requestError.message,
        });
      }
    } finally {
      bulkUploadingRef.current = false;
      setLoading((current) => ({ ...current, submittingBulk: false }));
    }
  }

  async function handleFacebookSync() {
    try {
      setLoading((current) => ({ ...current, syncingFacebook: true }));
      setBulkFeedback(INITIAL_BULK_FEEDBACK);
      const payload = await fetchJson("/api/crm/integrations/facebook-leads/", {
        method: "POST",
        body: JSON.stringify({ sync: true, run_cron: true, limit: 50 }),
      });
      const createdCount = Number(
        payload.created_count ??
          (payload.results || []).filter((item) => item.created).length,
      );
      const processedCount = payload.processed || 0;
      const startedCopy = payload.started
        ? "Facebook import job started. Sync history will update after the run completes."
        : "Facebook import completed.";
      setBulkFeedback({
        error: "",
        notice: payload.started
          ? startedCopy
          : `${startedCopy} Last run added ${createdCount} lead${createdCount === 1 ? "" : "s"} from ${processedCount} checked lead${processedCount === 1 ? "" : "s"}.`,
      });
      if (typeof setLastSyncAlert === "function") setLastSyncAlert(null);
      await Promise.all([loadWorkspace(), loadContext(), loadIntegrationDiagnostics()]);
    } catch (err) {
      setBulkFeedback({ error: err.message, notice: "" });
      if (typeof setLastSyncAlert === "function") {
        setLastSyncAlert({
          id: "latest-facebook-sync-error",
          tone: "danger",
          title: "Facebook sync failed",
          description: err.message,
        });
      }
    } finally {
      setLoading((current) => ({ ...current, syncingFacebook: false }));
    }
  }

  function handleDownloadTemplate(format) {
    const isReassign = bulkModal.mode === "reassign";
    const endpoint = isReassign
      ? "/api/crm/imports/templates/bulk-reassign/"
      : "/api/crm/imports/templates/bulk-upload/";
    downloadViaBrowser(`${endpoint}?format=${format}`);
  }

  async function handleFollowUpStatusUpdate(followUpId) {
    try {
      setError("");
      await fetchJson(`/api/crm/follow-ups/${followUpId}/`, {
        method: "PATCH",
        body: JSON.stringify({ status: "completed" }),
      });
      setNotice("Follow-up marked as completed.");
      await loadWorkspace();
      if (selectedLeadId) {
        await refreshSelectedLead(selectedLeadId);
      }
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function handleInlineDispositionSave(leadId, dispositionPayload, freshLead = null) {
    const now = new Date();
    const isTestDrivePayload = Boolean(
      dispositionPayload.scheduleOem ||
        dispositionPayload.scheduleDealer ||
        dispositionPayload.scheduleCity,
    );
    await fetchJson(`/api/crm/leads/${leadId}/log_call/`, {
      method: "POST",
      body: JSON.stringify({
        agent_id: dispositionPayload.agentId || null,
        disposition_id: dispositionPayload.dispositionId || null,
        next_follow_up_at: dispositionPayload.nextFollowUp
          ? new Date(dispositionPayload.nextFollowUp).toISOString()
          : null,
        remarks: dispositionPayload.remarks || "",
        status: "completed",
        sub_disposition_id: dispositionPayload.subDispositionId || null,
        ended_at: now.toISOString(),
        started_at: new Date(now.getTime() - 5000).toISOString(),
        ...(isTestDrivePayload
          ? {
              test_drive: {
                dealer_id: dispositionPayload.dealerId || null,
                oem: dispositionPayload.scheduleOem || "",
                dealer_name: dispositionPayload.scheduleDealer || "",
                city: dispositionPayload.scheduleCity || "",
                scheduled_for: dispositionPayload.nextFollowUp
                  ? new Date(dispositionPayload.nextFollowUp).toISOString()
                  : null,
              },
            }
          : {}),
      }),
    });

    const existingCf = (freshLead || selectedLead)?.custom_fields || {};
    const bookingAgent = agents.find(
      (a) => String(a.id) === String(dispositionPayload.agentId),
    );

    if (dispositionPayload.testDriveConducted) {
      const conductedHistory = Array.isArray(existingCf.test_drive_conducted_history)
        ? existingCf.test_drive_conducted_history
        : [];
      const tdHistory = Array.isArray(existingCf.test_drive_history)
        ? existingCf.test_drive_history
        : [];
      const lastScheduled = tdHistory.length > 0 ? tdHistory[tdHistory.length - 1] : null;
      const conductedEntry = {
        conducted_at: new Date().toISOString(),
        oem: lastScheduled?.oem || existingCf.test_drive_oem || "",
        dealer: lastScheduled?.dealer || existingCf.test_drive_dealer || "",
        city: lastScheduled?.city || existingCf.test_drive_city || "",
        schedule_date: lastScheduled?.date || existingCf.test_drive_date || "",
        agent_name:
          bookingAgent?.full_name ||
          (freshLead || selectedLead)?.assigned_agent?.full_name ||
          "",
        satisfied: dispositionPayload.satisfied,
      };

      const cfPatch = {
        ...existingCf,
        test_drive_conducted_history: [...conductedHistory, conductedEntry],
        test_drive_conducted_count: String(conductedHistory.length + 1),
        test_drive_conducted_latest: conductedEntry,
      };

      if (
        dispositionPayload.satisfied === false &&
        (dispositionPayload.scheduleOem || dispositionPayload.scheduleDealer)
      ) {
        const prevCount = parseInt(existingCf.test_drive_count || "0", 10);
        const newBooking = {
          date: dispositionPayload.nextFollowUp
            ? new Date(dispositionPayload.nextFollowUp).toISOString()
            : "",
          oem: dispositionPayload.scheduleOem || "",
          dealer: dispositionPayload.scheduleDealer || "",
          city: dispositionPayload.scheduleCity || "",
          agent_name:
            bookingAgent?.full_name ||
            selectedLead?.assigned_agent?.full_name ||
            "",
          booked_at: new Date().toISOString(),
          rebooked: true,
        };
        Object.assign(cfPatch, {
          test_drive_oem: newBooking.oem,
          test_drive_dealer: newBooking.dealer,
          test_drive_city: newBooking.city,
          test_drive_date: newBooking.date,
          test_drive_agent_name: newBooking.agent_name,
          test_drive_count: String(prevCount + 1),
          test_drive_history: [...tdHistory, newBooking],
        });
        await fetchJson(`/api/crm/leads/${leadId}/`, {
          method: "PATCH",
          body: JSON.stringify({ custom_fields: cfPatch, status: "test_drive_scheduled" }),
        });
      } else {
        await fetchJson(`/api/crm/leads/${leadId}/`, {
          method: "PATCH",
          body: JSON.stringify({ custom_fields: cfPatch }),
        });
      }
    } else if (dispositionPayload.isBookedDisposition) {
      const bookingHistory = Array.isArray(existingCf.booking_history)
        ? existingCf.booking_history
        : [];
      const newBookingEntry = {
        booked_at: new Date().toISOString(),
        date: dispositionPayload.bdDate || "",
        oem: dispositionPayload.bdOem || "",
        dealer: dispositionPayload.bdDealer || "",
        city:
          dispositionPayload.bdCity ||
          (freshLead || selectedLead)?.city_name ||
          "",
        agent_name:
          bookingAgent?.full_name ||
          (freshLead || selectedLead)?.assigned_agent?.full_name ||
          "",
      };
      await fetchJson(`/api/crm/leads/${leadId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          custom_fields: {
            ...existingCf,
            booking_oem: newBookingEntry.oem,
            booking_dealer: newBookingEntry.dealer,
            booking_city: newBookingEntry.city,
            booking_date: newBookingEntry.date,
            booking_agent_name: newBookingEntry.agent_name,
            booking_history: [...bookingHistory, newBookingEntry],
          },
        }),
      });
    } else if (dispositionPayload.isDeliveredDisposition) {
      const deliveryHistory = Array.isArray(existingCf.delivery_history)
        ? existingCf.delivery_history
        : [];
      const newDeliveryEntry = {
        delivered_at: new Date().toISOString(),
        date: dispositionPayload.bdDate || "",
        oem: dispositionPayload.bdOem || "",
        dealer: dispositionPayload.bdDealer || "",
        city:
          dispositionPayload.bdCity ||
          (freshLead || selectedLead)?.city_name ||
          "",
        agent_name:
          bookingAgent?.full_name ||
          (freshLead || selectedLead)?.assigned_agent?.full_name ||
          "",
      };
      await fetchJson(`/api/crm/leads/${leadId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          custom_fields: {
            ...existingCf,
            delivery_oem: newDeliveryEntry.oem,
            delivery_dealer: newDeliveryEntry.dealer,
            delivery_city: newDeliveryEntry.city,
            delivery_date: newDeliveryEntry.date,
            delivery_agent_name: newDeliveryEntry.agent_name,
            delivery_history: [...deliveryHistory, newDeliveryEntry],
          },
        }),
      });
    } else if (dispositionPayload.scheduleOem || dispositionPayload.scheduleDealer) {
      const prevCount = parseInt(existingCf.test_drive_count || "0", 10);
      const history = Array.isArray(existingCf.test_drive_history)
        ? existingCf.test_drive_history
        : [];
      const newBooking = {
        date: dispositionPayload.nextFollowUp
          ? new Date(dispositionPayload.nextFollowUp).toISOString()
          : "",
        oem: dispositionPayload.scheduleOem || "",
        dealer: dispositionPayload.scheduleDealer || "",
        city: dispositionPayload.scheduleCity || "",
        agent_name:
          bookingAgent?.full_name || selectedLead?.assigned_agent?.full_name || "",
        booked_at: new Date().toISOString(),
      };
      await fetchJson(`/api/crm/leads/${leadId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          custom_fields: {
            ...existingCf,
            test_drive_oem: newBooking.oem,
            test_drive_dealer: newBooking.dealer,
            test_drive_city: newBooking.city,
            test_drive_date: newBooking.date,
            test_drive_agent_name: newBooking.agent_name,
            test_drive_count: String(prevCount + 1),
            test_drive_history: [...history, newBooking],
          },
        }),
      });
    }
    try {
      await Promise.all([refreshSelectedLead(leadId), loadWorkspace({ silent: true })]);
    } catch (refreshError) {
      // The disposition has already been persisted. Do not report a refresh issue as a save failure.
      console.warn("Disposition saved, but the lead view could not be refreshed.", refreshError);
    }
    return true;
  }

  async function handleCreateFollowUp(leadId, payload) {
    try {
      await fetchJson("/api/crm/follow-ups/", {
        method: "POST",
        body: JSON.stringify({ lead: leadId, ...payload }),
      });
      if (leadId) {
        await Promise.all([refreshSelectedLead(leadId), loadWorkspace({ silent: true })]);
      }
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    }
  }

  async function handleUpdateTags(leadId, tags) {
    try {
      await fetchJson(`/api/crm/leads/${leadId}/`, {
        method: "PATCH",
        body: JSON.stringify({ tags }),
      });
      if (leadId) {
        await Promise.all([refreshSelectedLead(leadId), loadWorkspace({ silent: true })]);
      }
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    }
  }

  async function handleDocumentUpload(files, documentType) {
    if (!selectedLeadId || !selectedLead) return false;

    const validationError = validateDocumentSelection({
      allowedExtensions: DOCUMENT_ALLOWED_EXTENSIONS,
      existingCount: selectedLead.documents?.length || 0,
      files,
      maxDocuments: MAX_LEAD_DOCUMENTS,
    });

    if (validationError) {
      setDocumentFeedback({ error: validationError, notice: "" });
      return false;
    }

    try {
      setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
      setLoading((current) => ({ ...current, uploadingDocuments: true }));

      for (const file of files) {
        const formData = new FormData();
        formData.append("lead", String(selectedLeadId));
        formData.append("name", file.name);
        formData.append("document_type", documentType || "supporting_document");
        formData.append("file", file);
        await fetchJson("/api/crm/documents/", {
          method: "POST",
          body: formData,
        });
      }

      await Promise.all([refreshSelectedLead(selectedLeadId), loadWorkspace()]);
      const countLabel = `${files.length} document${files.length === 1 ? "" : "s"}`;
      setDocumentFeedback({ error: "", notice: `${countLabel} uploaded successfully.` });
      return true;
    } catch (requestError) {
      setDocumentFeedback({ error: requestError.message, notice: "" });
      return false;
    } finally {
      setLoading((current) => ({ ...current, uploadingDocuments: false }));
    }
  }

  async function handleLeadExport(format) {
    if (currentUser?.role !== "admin") return;
    const params = new URLSearchParams({ format });

    if (filters.registeredDate) params.set("registered_date", filters.registeredDate);
    if (filters.registeredDateEnd)
      params.set("registered_date_end", filters.registeredDateEnd);

    const stripStubs = (csv) =>
      (csv || "")
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v && !v.startsWith("__no_"))
        .join(",");
    const sourceCsv = stripStubs(filters.sourceId);
    if (sourceCsv) params.set("source", sourceCsv);

    if (filters.region) {
      const regionParts = filters.region
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);
      const hasOtherCities = regionParts.includes("Other Cities");
      const realRegions = regionParts.filter((r) => r !== "Other Cities");
      if (realRegions.length > 0) params.set("region", realRegions.join(","));
      if (hasOtherCities) {
        const otherCityNames = geographyData.otherCities || [];
        const existingCities = filters.city
          ? filters.city
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [];
        const mergedCities = Array.from(new Set([...existingCities, ...otherCityNames]));
        if (mergedCities.length > 0) params.set("city", mergedCities.join(","));
      } else if (filters.city) {
        params.set("city", filters.city);
      }
    } else if (filters.city) {
      params.set("city", filters.city);
    }

    const selectedDispositionId = filters.subDispositionId || filters.dispositionRootId;
    if (selectedDispositionId) params.set("disposition", selectedDispositionId);
    if (filters.status) params.set("status", filters.status);
    if (filters.customerStatus) params.set("customer_status", filters.customerStatus);
    if (filters.followUpBucket) params.set("follow_up_bucket", filters.followUpBucket);
    if (filters.timeLeft) params.set("time_left", filters.timeLeft);
    if (filters.attemptedCalls)
      params.set("attempted_calls_bucket", filters.attemptedCalls);
    if (filters.connectedCalls)
      params.set("connected_calls_bucket", filters.connectedCalls);
    if (filters.ordering) params.set("ordering", filters.ordering);

    if (roleScope?.scope === "own_leads") {
      params.set("assigned_to", String(currentUser.id));
    } else if (filters.assignedToId) {
      params.set("assigned_to", filters.assignedToId);
    }

    const hasFilters = [...params.keys()].some((k) => k !== "format");
    setNotice(`Preparing ${format.toUpperCase()} export…`);
    try {
      await downloadViaBrowser(`/api/crm/leads/export/?${params.toString()}`);
      setNotice(
        `${format.toUpperCase()} export downloaded${hasFilters ? " (filtered)" : ""}.`,
      );
    } catch (err) {
      setError(err.message || "Export failed. Please try again.");
    }
  }

  async function handleFullDataExport() {
    if (currentUser?.role !== "admin") return;
    try {
      const data = await fetchJson("/api/crm/leads/full-export/", { method: "POST" });
      setNotice("Full data export queued — it will download automatically when ready.");
      if (onExportJobQueued) onExportJobQueued(data);
    } catch (err) {
      setError(err.message || "Failed to queue export. Please try again.");
    }
  }

  function downloadInvalidRows(format, batchId) {
    setBulkFeedback({
      error: "",
      notice: `Preparing invalid-row ${format.toUpperCase()} download.`,
    });
    downloadViaBrowser(`/api/crm/imports/${batchId}/invalid-rows/?format=${format}`);
  }

  return (
    <>
      {showContent ? (
        <>
          <LeadFilters
            agents={agents}
            availableCities={filterCityOptions}
            availableRegions={geographyData.regionOptions}
            canBulkAssign={roleScope?.can_bulk_assign}
            canBulkUpload={roleScope?.can_bulk_upload}
            canCreateLead={roleScope?.can_create_lead}
            canExportLeads={currentUser?.role === "admin"}
            availableSubDispositions={availableFilterSubDispositions}
            filters={filters}
            leadSources={context.lead_sources}
            onAddLead={openCreateLeadModal}
            onBulkAssign={openBulkReassign}
            onBulkUpload={openBulkUpload}
            onClearFilters={clearLeadFilters}
            onDownloadLeads={handleLeadExport}
            onFullDataExport={handleFullDataExport}
            onFilterChange={handleLeadFilterChange}
            onSearchChange={(value) => {
              setSearch(value);
              setFilters((current) => ({ ...current, page: 1 }));
            }}
            role={currentUser?.role}
            roleScope={roleScope}
            rootDispositions={rootDispositions}
            search={search}
          />

          <LeadsTable
            canDeleteLead={currentUser?.role === "admin"}
            currentPage={filters.page}
            leads={leadResponse.results}
            loading={loading.workspace}
            pageSize={leadPageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            paginationItems={paginationItems}
            roleScope={roleScope}
            selectedLeadId={selectedLeadId}
            showOthersDetail={Boolean(filters.showOthersDetail)}
            sourceMap={sourceMap}
            totalCount={leadResponse.count}
            onDeleteLead={handleDeleteLead}
            onEditLead={openEditLeadModal}
            onLogCall={openCallModal}
            onOpenJourney={openJourneyModal}
            onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
            onPageSizeChange={(pageSize) => {
              setLeadPageSize(pageSize);
              setFilters((current) => ({ ...current, page: 1 }));
            }}
            onSelectLead={(leadId) => setSelectedLeadId(String(leadId))}
          />
        </>
      ) : null}

      <LeadFormModal
        agents={leadModalAgents}
        availableCities={leadCityOptions}
        availableRegions={geographyData.regionOptions}
        dispositionGroups={context.disposition_groups}
        isBtlUser={currentUser?.role === "btl"}
        leadForm={leadForm}
        leadModal={leadModal}
        leadSources={context.lead_sources}
        loading={loading.submittingLead}
        loadingAgents={loading.assignableAgents}
        onChange={handleLeadFormChange}
        onClose={() => setLeadModal({ open: false, mode: "create" })}
        onSubmit={handleLeadSubmit}
        roleScope={roleScope}
        selectedLead={selectedLead}
      />

      <BulkUploadModal
        bulkForm={bulkForm}
        bulkFeedback={bulkFeedback}
        bulkResult={bulkResult}
        facebookDiagnostics={integrationDiagnostics?.facebook}
        leadSources={context.lead_sources}
        loading={loading.submittingBulk}
        mode={bulkModal.mode}
        onChange={handleBulkFormChange}
        onClose={closeBulkModal}
        onDownloadInvalid={downloadInvalidRows}
        onDownloadTemplate={handleDownloadTemplate}
        onSyncFacebook={handleFacebookSync}
        onSubmit={handleBulkSubmit}
        open={bulkModal.open}
        syncingFacebook={loading.syncingFacebook}
      />

      <LeadJourneyModal
        agents={agents}
        dispositionGroups={context.disposition_groups}
        documentFeedback={documentFeedback}
        documentUploadLoading={loading.uploadingDocuments}
        fetchJson={fetchJson}
        lead={selectedLead}
        loading={loading.detail}
        onClose={() => setJourneyModalOpen(false)}
        onCreateFollowUp={handleCreateFollowUp}
        onLogCall={(leadId) => {
          setJourneyModalOpen(false);
          openCallModal(leadId);
        }}
        onSaveDisposition={handleInlineDispositionSave}
        onUpdateTags={handleUpdateTags}
        onUploadDocuments={handleDocumentUpload}
        open={journeyModalOpen}
        roleScope={roleScope}
        isAdmin={currentUser?.role === "admin"}
      />

      <LeadJourneyPickerModal
        leads={leadResponse.results}
        onClose={() => setJourneyPickerOpen(false)}
        onConfirm={handleJourneyPickerConfirm}
        open={journeyPickerOpen}
        selectedLeadId={selectedLeadId}
      />

      <DuplicateAlertModal
        leadId={duplicateState.leadId}
        matches={duplicateState.matches}
        message={duplicateState.message}
        onClose={() => setDuplicateState(INITIAL_DUPLICATE_STATE)}
        onConfirm={handleDuplicateConfirm}
        onOpenJourney={() => {
          const targetLeadId = duplicateState.leadId;
          setDuplicateState(INITIAL_DUPLICATE_STATE);
          if (targetLeadId) {
            openJourneyModal(targetLeadId);
          }
        }}
        open={duplicateState.open}
        referenceLead={duplicateState.payload || selectedLead}
        roleScope={roleScope}
        variant={duplicateState.variant}
      />

    </>
  );
}

export default LeadsPage;
